import React, { useRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import ButtonWithIcon from '../../buttons/button-with-icon';
import { AccessibleMenuWithArrows } from '../../assessments/actions/AccessibleMenuWithArrows';
import { popoverClassName, ulClassName } from '../../basic/simple-actions';
import { LABEL_BUTTON_CLASS } from 'chameleon/legacy/src/components/TeacherOrganize/utils/constants';
import PopOver from 'chameleon/ui-stack/ui/base/popover';
import Button from '../../base/button';
import './LabelNavItem.scss';
import { useDroppable } from '@dnd-kit/core';

const LabelNavItem = (props) => {
  const {
    id,
    tag,
    item,
    accessibility,
    handleKeyDown,
    children,
    onEditLabel,
    onDeleteLabel,
    onCreateLabel,
    showActions = true,
    isReordering = false,
    onMoveUp,
    onMoveDown,
    isTeacherInfoLoading,
  } = props;
  const NavTag = tag;
  const { name, isActive, onClick, parentId, index, labelId } = item;

  const triggerId = `label-${labelId}-actions`;
  const label = (
    <ButtonWithIcon
      isIcon
      isNaked
      id={triggerId}
      aria-label={`Actions for ${name}`}
      icon="more-actions"
      className={LABEL_BUTTON_CLASS.menu}
      data-test-id={`${LABEL_BUTTON_CLASS.menu}-${labelId}`}
    />
  );
  const popover = useRef(null);

  const LabelActionItem = ({
    name: actionName,
    onClickHandler,
    dataTestId,
    isDisabled = false,
    className: extraButtonClasses = '',
  }) => (
    <Button
      onClick={onClickHandler}
      className={`w-full flex items-center mt-px h-14 ${extraButtonClasses}`}
      isRounded={false}
      dataTestId={dataTestId}
      disabled={isDisabled}
      isNaked
    >
      {actionName}
    </Button>
  );

  const { isOver, setNodeRef } = useDroppable({
    id: `label/${item.labelId}`,
    data: {
      id: labelId,
      name,
      supports: ['assessment'],
    },
  });

  const LabelLink = () => (
    <a href="#" onClick={(e) => e.preventDefault()} tabIndex="0">
      {name}
    </a>
  );

  return (
    <Fragment>
      <NavTag
        id={id}
        ref={setNodeRef}
        className={cn(
          'flex text-gray-900 relative labels-popover cursor-pointer hover:bg-blue-500 hover:bg-opacity-10',
          {
            'bg-blue-600 bg-opacity-10 underline': isActive,
            'bg-blue-500 bg-opacity-10': isOver,
          },
        )}
        aria-current={isActive ? 'page' : undefined}
        onKeyDown={(event) => handleKeyDown(event, item)}
        role="listitem"
        {...accessibility}
        data-accessibility-index={index}
        data-label-id={labelId}
        onClick={onClick}
      >
        <span
          className={cn(
            'w-full items-center label text-base pt-4.3 pb-4.3 break-words',
            {
              'pl-8': !Boolean(parentId),
              'pl-16': Boolean(parentId),
            },
          )}
        >
          <LabelLink />
        </span>
        {showActions && (
          <PopOver
            withPortal
            tabIndex={-1}
            ref={popover}
            contentClassName={popoverClassName}
            trigger={label}
            triggerId={triggerId}
            className="mt-2"
            hideArrow
            isNarrow
            alwaysClose
            disabled={isTeacherInfoLoading}
          >
            <AccessibleMenuWithArrows ulClassName={ulClassName}>
              <Fragment>
                <LabelActionItem
                  name="Move Label Up"
                  dataTestId={`${LABEL_BUTTON_CLASS.moveup}-${labelId}`}
                  onClickHandler={onMoveUp}
                  isDisabled={
                    Number.parseInt(accessibility['aria-posinset'], 10) === 1 ||
                    isReordering
                  }
                />
                <LabelActionItem
                  name="Move Label Down"
                  dataTestId={`${LABEL_BUTTON_CLASS.movedown}-${labelId}`}
                  onClickHandler={onMoveDown}
                  isDisabled={
                    accessibility['aria-posinset'] ===
                      accessibility['aria-setsize'] || isReordering
                  }
                  className="border-b border-gray-400"
                />
                <LabelActionItem
                  name="Edit Label"
                  dataTestId={`${LABEL_BUTTON_CLASS.edit}-${labelId}`}
                  onClickHandler={() => onEditLabel(item)}
                />
                <LabelActionItem
                  name="Delete Label"
                  dataTestId={`${LABEL_BUTTON_CLASS.delete}-${labelId}`}
                  onClickHandler={() => onDeleteLabel(item)}
                />
                <LabelActionItem
                  name="New Label"
                  dataTestId={`${LABEL_BUTTON_CLASS.create}-${labelId}`}
                  onClickHandler={() => onCreateLabel(parentId)}
                />
              </Fragment>
            </AccessibleMenuWithArrows>
          </PopOver>
        )}
      </NavTag>
      {children}
    </Fragment>
  );
};

LabelNavItem.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  tag: PropTypes.string,
  item: PropTypes.shape({
    name: PropTypes.string.isRequired,
    isActive: PropTypes.bool.isRequired,
    onClick: PropTypes.func,
    parentId: PropTypes.any,
    index: PropTypes.number.isRequired,
    labelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  accessibility: PropTypes.object,
  handleKeyDown: PropTypes.func,
  children: PropTypes.object,
  showActions: PropTypes.bool,
  onMoveUp: PropTypes.func,
  onMoveDown: PropTypes.func,
};

LabelNavItem.defaultProps = {
  id: null,
  className: null,
  tag: 'li',
  accessibility: {},
  handleKeyDown: () => {},
  showActions: true,
  children: null,
  item: {
    onClick: () => {},
  },
  onMoveUp: () => {},
  onMoveDown: () => {},
};

export default LabelNavItem;
